import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import type { ColumnDef } from "@tanstack/react-table";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { BookOpen, Edit3, Loader2, MoreHorizontal, Plus, Tags, Trash2 } from "lucide-react";

import { AdviceFormSheet } from "@/features/advice/presentation/components/AdviceFormSheet";
import { ConfirmDialog } from "@/shared/components/data/ConfirmDialog";
import { DataTable } from "@/shared/components/data/DataTable";
import { DataToolbar } from "@/shared/components/data/DataToolbar";
import { Pagination } from "@/shared/components/data/Pagination";
import { StatusPill } from "@/shared/components/data/StatusPill";
import { PageHeader } from "@/shared/components/layout/PageHeader";
import { Button } from "@/shared/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { Switch } from "@/shared/components/ui/switch";
import { useDebouncedValue } from "@/shared/hooks/use-debounced-value";
import { useQueryState } from "@/shared/hooks/use-query-state";
import { toast } from "@/shared/lib/toast";
import { cn } from "@/shared/lib/utils";
import {
  useDeleteAdviceMutation,
  useListAdviceCategoriesQuery,
  useListAdviceQuery,
  useUpdateAdviceMutation,
} from "@/store/apis/adminApi.enhanced";
import type { Advice } from "@/store/apis/adminApi.enhanced";
import type { ListAdviceApiArg } from "@/store/apis/adminApi.generated";

type AdviceSort = NonNullable<ListAdviceApiArg["sort"]>;

const DEFAULT_LIMIT = 25;

/**
 * Lista de consejos — CRUD completo.
 *
 * URL es la fuente de verdad para search/filtros/offset — compartir
 * un link lleva el estado:
 *   /consejos?q=...&cat=...&active=1&sort=-updated_at&offset=50&limit=25
 */
export function AdvicePage() {
  const { get, set } = useQueryState();

  // ─── Query state ────────────────────────────────────────────
  const search = get("q", "");
  const debouncedSearch = useDebouncedValue(search, 300);
  const category = get("cat", "");
  const activeFilter = get("active", ""); // "" | "1" | "0"
  const sort = (get("sort", "-updated_at") || "-updated_at") as AdviceSort;
  const offset = Number(get("offset", "0")) || 0;
  const limit = Number(get("limit", String(DEFAULT_LIMIT))) || DEFAULT_LIMIT;

  const filtersActive =
    debouncedSearch.trim().length > 0 || Boolean(category) || activeFilter !== "";

  // ─── Queries ────────────────────────────────────────────────
  const { data, isLoading, isFetching, isError, error, refetch } = useListAdviceQuery({
    q: debouncedSearch || undefined,
    category: category || undefined,
    active: activeFilter === "" ? undefined : activeFilter === "1",
    sort,
    offset,
    limit,
  });

  const categoriesQuery = useListAdviceCategoriesQuery({ limit: 100, sort: "position" });
  const categoryBySlug = useMemo(() => {
    const map = new Map<string, { label: string; emoji?: string | null }>();
    for (const c of categoriesQuery.data?.items ?? []) {
      map.set(c.slug, { label: c.label, emoji: c.emoji });
    }
    return map;
  }, [categoriesQuery.data]);

  // ─── Mutations ──────────────────────────────────────────────
  const [updateAdvice, updateState] = useUpdateAdviceMutation();
  const [deleteAdvice] = useDeleteAdviceMutation();

  // Toggle active inline — optimistic UX via toast.promise. Si falla,
  // RTK Query revierte con el refetch del tag `Advice`.
  const [togglingId, setTogglingId] = useState<number | null>(null);
  const handleToggle = async (advice: Advice) => {
    setTogglingId(advice.id);
    try {
      await updateAdvice({
        id: advice.id,
        body: { active: !advice.active },
      }).unwrap();
      toast.success(advice.active ? "Consejo pausado" : "Consejo activado");
    } catch (err) {
      toast.error(err);
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async (advice: Advice) => {
    try {
      await deleteAdvice({ id: advice.id }).unwrap();
      toast.success("Consejo eliminado");
    } catch (err) {
      toast.error(err);
      throw err; // Re-lanzamos para que ConfirmDialog quede abierto.
    }
  };

  // ─── Sheet state ────────────────────────────────────────────
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editing, setEditing] = useState<Advice | null>(null);

  const openCreate = () => {
    setEditing(null);
    setSheetOpen(true);
  };
  const openEdit = (advice: Advice) => {
    setEditing(advice);
    setSheetOpen(true);
  };

  // ─── Columns ────────────────────────────────────────────────
  const columns = useMemo<ColumnDef<Advice>[]>(
    () => [
      {
        id: "body",
        header: "Consejo",
        accessorKey: "body",
        cell: ({ row }) => (
          <div className="min-w-0 max-w-xl">
            <p className="line-clamp-2 text-[13.5px] leading-relaxed text-foreground">
              {row.original.body}
            </p>
          </div>
        ),
      },
      {
        id: "category",
        header: "Categoría",
        accessorKey: "category",
        cell: ({ row }) => {
          const cat = categoryBySlug.get(row.original.category);
          return (
            <span className="inline-flex items-center gap-1.5 text-[13px] text-foreground">
              {cat?.emoji ? <span aria-hidden>{cat.emoji}</span> : null}
              <span>{cat?.label ?? row.original.category}</span>
            </span>
          );
        },
      },
      {
        id: "position",
        accessorKey: "position",
        header: "Pos.",
        cell: ({ row }) => (
          <span className="font-mono text-[12px] tabular-nums text-muted-foreground">
            {row.original.position}
          </span>
        ),
      },
      {
        id: "active",
        header: "Estado",
        cell: ({ row }) => {
          const advice = row.original;
          const loading = togglingId === advice.id && updateState.isLoading;
          return (
            <div className="inline-flex items-center gap-2.5">
              {loading ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
              ) : (
                <Switch
                  checked={advice.active}
                  onCheckedChange={() => void handleToggle(advice)}
                  aria-label={advice.active ? "Pausar consejo" : "Activar consejo"}
                />
              )}
              <StatusPill tone={advice.active ? "success" : "muted"}>
                {advice.active ? "Activo" : "Pausado"}
              </StatusPill>
            </div>
          );
        },
      },
      {
        id: "updated_at",
        header: "Actualizado",
        cell: ({ row }) => {
          const d = row.original.updated_at ?? row.original.created_at;
          return (
            <span className="font-mono text-[12px] text-muted-foreground">
              {d ? format(new Date(d), "d MMM yyyy", { locale: es }) : "—"}
            </span>
          );
        },
      },
      {
        id: "actions",
        header: () => <span className="sr-only">Acciones</span>,
        cell: ({ row }) => (
          <div className="flex justify-end">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <MoreHorizontal className="h-4 w-4" />
                  <span className="sr-only">Acciones</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => openEdit(row.original)}>
                  <Edit3 className="h-3.5 w-3.5" />
                  Editar
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <ConfirmDialog
                  trigger={
                    <DropdownMenuItem
                      onSelect={(e) => e.preventDefault()}
                      className="text-destructive focus:text-destructive"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      Eliminar
                    </DropdownMenuItem>
                  }
                  title="Eliminar consejo"
                  description={
                    <>
                      Se eliminará el consejo{" "}
                      <strong className="text-foreground">#{row.original.id}</strong>. Esta acción
                      no se puede deshacer.
                    </>
                  }
                  confirmLabel="Sí, eliminar"
                  onConfirm={() => handleDelete(row.original)}
                />
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ),
      },
    ],
    // Dependencias: la lookup de categorías + estado del toggle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [categoryBySlug, togglingId, updateState.isLoading],
  );

  return (
    <>
      <PageHeader
        kicker="Contenido"
        title="Consejos diarios"
        description="Gestiona el banco de consejos que aparecen en el card 'Consejo del día' del home móvil."
        actions={
          <>
            <Button asChild variant="outline" size="sm">
              <Link to="/consejos/categorias">
                <Tags className="h-3.5 w-3.5" />
                Categorías
              </Link>
            </Button>
            <Button size="sm" onClick={openCreate}>
              <Plus className="h-3.5 w-3.5" />
              Nuevo consejo
            </Button>
          </>
        }
      />

      <DataToolbar
        search={search}
        onSearchChange={(next) => set({ q: next, offset: 0 })}
        searchPlaceholder="Buscar en el texto del consejo…"
        filters={
          <>
            <Select
              value={category || "all"}
              onValueChange={(v) => set({ cat: v === "all" ? "" : v, offset: 0 })}
            >
              <SelectTrigger className="h-10 w-[180px] text-[13px]">
                <SelectValue placeholder="Categoría" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las categorías</SelectItem>
                {categoriesQuery.data?.items.map((c) => (
                  <SelectItem key={c.slug} value={c.slug}>
                    <span className="inline-flex items-center gap-2">
                      {c.emoji ? <span aria-hidden>{c.emoji}</span> : null}
                      <span>{c.label}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={activeFilter || "all"}
              onValueChange={(v) => set({ active: v === "all" ? "" : v, offset: 0 })}
            >
              <SelectTrigger className="h-10 w-[140px] text-[13px]">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="1">Activos</SelectItem>
                <SelectItem value="0">Pausados</SelectItem>
              </SelectContent>
            </Select>

            <Select value={sort ?? "-updated_at"} onValueChange={(v) => set({ sort: v })}>
              <SelectTrigger className="h-10 w-[180px] text-[13px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="-updated_at">Más recientes</SelectItem>
                <SelectItem value="updated_at">Más antiguos</SelectItem>
                <SelectItem value="position">Por posición ↑</SelectItem>
                <SelectItem value="-position">Por posición ↓</SelectItem>
                <SelectItem value="-created_at">Creado reciente</SelectItem>
                <SelectItem value="created_at">Creado antiguo</SelectItem>
              </SelectContent>
            </Select>
          </>
        }
        actions={
          isFetching && !isLoading ? (
            <span
              className={cn(
                "inline-flex items-center gap-1.5 font-mono text-[11px] text-muted-foreground",
              )}
            >
              <Loader2 className="h-3 w-3 animate-spin" />
              Actualizando
            </span>
          ) : null
        }
      />

      <DataTable
        columns={columns}
        data={data?.items}
        loading={isLoading}
        error={isError ? error : undefined}
        onRetry={() => void refetch()}
        onRowClick={openEdit}
        getRowId={(row) => String(row.id)}
        filtersActive={filtersActive}
        empty={{
          icon: <BookOpen className="h-6 w-6" />,
          title: "Aún no hay consejos",
          description: "Crea el primer consejo para que empiece a aparecer en la app.",
          actions: (
            <Button onClick={openCreate}>
              <Plus className="h-3.5 w-3.5" />
              Nuevo consejo
            </Button>
          ),
        }}
        noResults={{
          actions: (
            <Button
              variant="outline"
              size="sm"
              onClick={() => set({ q: "", cat: "", active: "", offset: 0 })}
            >
              Limpiar filtros
            </Button>
          ),
        }}
      />

      <Pagination
        total={data?.total ?? 0}
        limit={limit}
        offset={offset}
        onOffsetChange={(next) => set({ offset: next })}
        onLimitChange={(next) => set({ limit: next })}
      />

      <AdviceFormSheet open={sheetOpen} onOpenChange={setSheetOpen} advice={editing} />
    </>
  );
}
